import React from 'react';
import { BarChart3, Clock, Grid3x3, Trophy } from 'lucide-react';
import { UserActivityDay } from '../../services/userActivityTypes';
import { ProjectDurationRank, WEEKDAY_LABELS, formatDay, formatDuration } from '../../services/userActivityStats';

// Diagramme der Nutzeraktivitaet. Handgebaut aus div/Flex und SVG im Stil der
// uebrigen Cockpit-Charts (AdminDashboard.tsx, crm/QuoteCharts.tsx) — das
// Projekt zieht bewusst keine Chart-Library.
//
// Rein praesentational: alle Serien kommen fertig gerechnet aus
// services/userActivityStats.ts bzw. hooks/useUserActivity.ts.

const LEER_TEXT = 'Keine Daten im Zeitraum';

const Panel: React.FC<{ title: string; icon: React.ReactNode; hint?: string; children: React.ReactNode }> = ({
    title,
    icon,
    hint,
    children,
}) => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">{icon} {title}</h3>
            {hint && <span className="text-[11px] text-slate-400 text-right">{hint}</span>}
        </div>
        {children}
    </div>
);

const Empty: React.FC<{ text?: string }> = ({ text }) => (
    <div className="py-12 text-center text-slate-300 text-xs font-medium">{text || LEER_TEXT}</div>
);

/** 'YYYY-MM-DD' -> 'dd.MM.' (Achsenbeschriftung, das Jahr steht im Filter). */
const tagKurz = (day: string): string => formatDay(day).slice(0, 6);

/** Index 0 = Montag, wie im Datenvertrag (userActivityStats.WEEKDAY_LABELS). */
const WOCHENTAGE = WEEKDAY_LABELS;

// „Projekte geöffnet" zählt ohne Wiederaufnahmen aus dem Hintergrund (ein
// App-Wechsel am Handy ist keine Projektöffnung). Das steht an jedem Diagramm,
// das diese Reihe zeigt — sonst wundert sich der Leser über die Differenz zur
// Zeitachse, in der die Wiederaufnahmen als eigene Sitzungen stehen.
const OHNE_WIEDERAUFNAHMEN = 'ohne Wiederaufnahmen';

// ─── 1. Aktivitaet je Tag ───────────────────────────────────────────────────

const TAGES_SERIEN: { key: 'startCount' | 'projectOpenCount'; label: string; cls: string }[] = [
    { key: 'startCount', label: 'App-Starts', cls: 'bg-indigo-500' },
    { key: 'projectOpenCount', label: 'Projekte geöffnet', cls: 'bg-blue-400' },
];

export const DailyActivityChart: React.FC<{ days: UserActivityDay[] }> = ({ days }) => {
    const max = Math.max(1, ...days.map((d) => Math.max(d.startCount, d.projectOpenCount)));

    return (
        <Panel
            title="Aktivität je Tag"
            icon={<BarChart3 className="w-4 h-4 text-blue-500" />}
            hint={`App-Starts und geöffnete Projekte · ${OHNE_WIEDERAUFNAHMEN}`}
        >
            {days.length === 0 ? (
                <Empty />
            ) : (
                <div className="p-5">
                    <div className="flex flex-wrap gap-4 mb-4">
                        {TAGES_SERIEN.map((s) => (
                            <span key={s.key} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                                <span className={`w-2.5 h-2.5 rounded-sm ${s.cls}`} /> {s.label}
                            </span>
                        ))}
                    </div>
                    <div className="overflow-x-auto">
                        <div className="flex items-end gap-2 min-w-max">
                            {days.map((d) => (
                                <div key={d.day} className="flex flex-col items-center gap-1.5 w-11 shrink-0">
                                    <span className="text-[10px] font-bold text-slate-400 tabular-nums h-3">
                                        {d.startCount + d.projectOpenCount > 0 ? d.startCount + d.projectOpenCount : ''}
                                    </span>
                                    <div
                                        className="w-full h-32 flex items-end justify-center gap-0.5"
                                        title={`${tagKurz(d.day)} — ${d.startCount} Start(s), ${d.projectOpenCount} Projektöffnung(en)`}
                                    >
                                        {TAGES_SERIEN.map((s) => (
                                            <div
                                                key={s.key}
                                                className={`w-1/2 rounded-t-md ${d[s.key] > 0 ? s.cls : 'bg-slate-100'}`}
                                                style={{ height: `${Math.max(2, (d[s.key] / max) * 100)}%` }}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">
                                        {tagKurz(d.day)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Panel>
    );
};

// ─── 2. Oeffnungen je Tagesstunde ───────────────────────────────────────────

export const HourHistogramChart: React.FC<{ hours: number[] }> = ({ hours }) => {
    const werte = hours.length === 24 ? hours : new Array(24).fill(0);
    const summe = werte.reduce((a, b) => a + b, 0);
    const max = Math.max(1, ...werte);

    return (
        <Panel
            title="Öffnungen je Tagesstunde"
            icon={<Clock className="w-4 h-4 text-indigo-500" />}
            hint={`lokale Uhrzeit, 0–23 Uhr · ${OHNE_WIEDERAUFNAHMEN}`}
        >
            {summe === 0 ? (
                <Empty />
            ) : (
                <div className="p-5">
                    <div className="flex items-end gap-1">
                        {werte.map((v, h) => (
                            <div key={h} className="flex-1 flex flex-col items-center gap-1">
                                <span className="text-[9px] font-bold text-slate-400 tabular-nums h-3">
                                    {v > 0 ? v : ''}
                                </span>
                                <div className="w-full h-28 flex items-end" title={`${String(h).padStart(2, '0')}:00 Uhr — ${v} Öffnung(en)`}>
                                    <div
                                        className={`w-full rounded-t-md ${v > 0 ? 'bg-indigo-500' : 'bg-slate-100'}`}
                                        style={{ height: `${Math.max(2, (v / max) * 100)}%` }}
                                    />
                                </div>
                                <span className="text-[9px] font-bold text-slate-400 tabular-nums">
                                    {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-2">Stunde des Tages</p>
                </div>
            )}
        </Panel>
    );
};

// ─── 3. Heatmap Wochentag x Stunde ──────────────────────────────────────────

/** Fuenf Stufen statt eines Farbverlaufs: eine Legende muss lesbar bleiben. */
const heatKlasse = (v: number, max: number): string => {
    if (v <= 0) return 'bg-slate-50';
    const anteil = v / max;
    if (anteil > 0.75) return 'bg-indigo-700';
    if (anteil > 0.5) return 'bg-indigo-500';
    if (anteil > 0.25) return 'bg-indigo-400';
    return 'bg-indigo-200';
};

export const WeekdayHourHeatmap: React.FC<{ matrix: number[][] }> = ({ matrix }) => {
    const zeilen = matrix.length === 7 ? matrix : [];
    const max = Math.max(0, ...zeilen.flatMap((r) => r));

    return (
        <Panel
            title="Wochentag × Stunde"
            icon={<Grid3x3 className="w-4 h-4 text-violet-500" />}
            hint={`Projektöffnungen ${OHNE_WIEDERAUFNAHMEN}, Montag oben`}
        >
            {max === 0 ? (
                <Empty />
            ) : (
                <div className="p-5">
                    <div className="overflow-x-auto">
                        <div className="min-w-max">
                            <div className="flex items-center gap-0.5 mb-1 pl-8">
                                {Array.from({ length: 24 }, (_, h) => (
                                    <span
                                        key={h}
                                        className="w-5 text-center text-[9px] font-bold text-slate-400 tabular-nums"
                                    >
                                        {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                                    </span>
                                ))}
                            </div>
                            {zeilen.map((row, wd) => (
                                <div key={wd} className="flex items-center gap-0.5 mb-0.5">
                                    <span className="w-8 shrink-0 text-[10px] font-bold text-slate-500">
                                        {WOCHENTAGE[wd]}
                                    </span>
                                    {row.map((v, h) => (
                                        <span
                                            key={h}
                                            className={`w-5 h-5 rounded-sm ${heatKlasse(v, max)}`}
                                            title={`${WOCHENTAGE[wd]} ${String(h).padStart(2, '0')}:00 Uhr — ${v} Öffnung(en)`}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <span className="text-[11px] font-bold text-slate-400">wenig</span>
                        {['bg-slate-50', 'bg-indigo-200', 'bg-indigo-400', 'bg-indigo-500', 'bg-indigo-700'].map((c) => (
                            <span key={c} className={`w-5 h-3 rounded-sm border border-slate-200 ${c}`} />
                        ))}
                        <span className="text-[11px] font-bold text-slate-400">viel (max. {max})</span>
                    </div>
                </div>
            )}
        </Panel>
    );
};

// ─── 4. Rangliste Top-Projekte ──────────────────────────────────────────────

/**
 * `sessionCount` zaehlt ALLE Sitzungen des Projekts, offene eingeschlossen.
 * Ausgewiesen wird hier aber die erfasste Dauer — und die kennt nur
 * abgeschlossene Sitzungen. Ohne diese Rechnung las sich ein Projekt mit
 * ausschliesslich offenen Sitzungen als „0 min · 2×", also als gemessene Null
 * statt als „noch offen".
 */
const abgeschlossene = (r: ProjectDurationRank): number => Math.max(0, r.sessionCount - r.openSessionCount);

const ranglisteTitel = (r: ProjectDurationRank): string => {
    const offen = r.openSessionCount > 0 ? `, ${r.openSessionCount} offene Sitzung(en) ohne Dauer` : '';
    const zahl = abgeschlossene(r);
    return zahl === 0
        ? `Keine abgeschlossene Sitzung — Dauer unbekannt${offen}`
        : `${formatDuration(r.totalMs)} in ${zahl} abgeschlossenen Sitzung(en)${offen}`;
};

export const TopProjectsChart: React.FC<{ rows: ProjectDurationRank[] }> = ({ rows }) => {
    const max = Math.max(1, ...rows.map((r) => r.totalMs));

    return (
        <Panel
            title="Top-Projekte nach Verweildauer"
            icon={<Trophy className="w-4 h-4 text-emerald-500" />}
            hint="nur abgeschlossene Sitzungen"
        >
            {rows.length === 0 ? (
                <Empty />
            ) : (
                <div className="p-5 space-y-2.5">
                    {rows.map((r) => {
                        const zahl = abgeschlossene(r);
                        return (
                            <div key={r.projectId} className="flex items-center gap-3">
                                <span
                                    className="w-44 shrink-0 text-[11px] font-bold text-slate-500 truncate"
                                    title={`${r.projectNumber ? `#${r.projectNumber} · ` : ''}${r.projectName || 'Unbenanntes Projekt'}`}
                                >
                                    {r.projectNumber ? `#${r.projectNumber} ` : ''}
                                    {r.projectName || 'Unbenanntes Projekt'}
                                </span>
                                <div
                                    className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden"
                                    title={ranglisteTitel(r)}
                                >
                                    {zahl > 0 && (
                                        <div
                                            className="h-full bg-emerald-500 rounded-lg"
                                            style={{ width: `${Math.max(2, (r.totalMs / max) * 100)}%` }}
                                        />
                                    )}
                                </div>
                                <span className="w-36 shrink-0 text-right text-[11px] tabular-nums leading-tight">
                                    {zahl === 0 ? (
                                        <span className="font-bold text-amber-600">keine abgeschlossene Sitzung</span>
                                    ) : (
                                        <>
                                            <span className="font-black text-slate-700">{formatDuration(r.totalMs)}</span>
                                            <span className="text-slate-400"> · {zahl}×</span>
                                        </>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                    {rows.some((r) => r.openSessionCount > 0) && (
                        <p className="text-[11px] text-slate-400 pt-1">
                            Offene Sitzungen zählen hier nicht mit — ihre Dauer ist unbekannt und wird nicht geschätzt.
                            Zeilen ohne eine einzige abgeschlossene Sitzung sind entsprechend gekennzeichnet.
                        </p>
                    )}
                </div>
            )}
        </Panel>
    );
};
